import React from "react";
import {
  Box,
  Button,
  Badge,
  Checkbox,
  Divider,
  Flex,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import reviewApi from "../api/reviewApi";
import { useAuth } from "../../../context/AuthContext";
import ModulePageShell from "../components/common/ModulePageShell";
import LoadingState from "../components/common/LoadingState";
import EmptyState from "../components/common/EmptyState";
import OperationalPanel from "../components/common/OperationalPanel";
import StatusBadge from "../components/common/StatusBadge";
import ReviewProgressTracker from "../components/reviews/ReviewProgressTracker";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import { formatDate, personName } from "../utils/normalize";

const ACTOR_MODES = {
  EMPLOYEE: "EMPLOYEE",
  MANAGER: "MANAGER",
  SECOND_LEVEL_MANAGER: "SECOND_LEVEL_MANAGER",
  HR: "HR",
  CALIBRATOR: "CALIBRATOR",
  ADMIN: "ADMIN",
  READ_ONLY: "READ_ONLY",
};

const FINAL_STATES = new Set(["FINALIZED", "COMPLETED", "CLOSED", "ACKNOWLEDGED"]);
const EMPLOYEE_EDIT_STATES = new Set([
  "DRAFT",
  "SELF_ASSESSMENT",
  "SELF_ASSESSMENT_IN_PROGRESS",
  "EMPLOYEE_REVIEW",
  "NOT_STARTED",
]);
const MANAGER_EDIT_STATES = new Set([
  "SELF_ASSESSMENT_SUBMITTED",
  "MANAGER_REVIEW",
  "MANAGER_ASSESSMENT",
  "MANAGER_REVIEW_IN_PROGRESS",
]);
const SECOND_LEVEL_EDIT_STATES = new Set([
  "SECOND_LEVEL_REVIEW",
  "SECOND_LEVEL_REVIEW_IN_PROGRESS",
]);
const CALIBRATION_STATES = new Set(["CALIBRATION", "PENDING_CALIBRATION", "CALIBRATED"]);

const getEntityId = (value) => value?._id || value?.id || value || null;

const idsMatch = (left, right) =>
  Boolean(left && right && String(left) === String(right));

const normalizeSectionKey = (section = {}) =>
  String(section.sectionId || section.code || section._id || section.id || "").toUpperCase();

const normalizeReviewItem = (item = {}, index = 0) => ({
  ...item,
  itemId: item.itemId || item.itemCode || item._id || item.id || `ITEM_${index + 1}`,
  title: item.title || item.name || item.label || `Item ${index + 1}`,
  description: item.description || item.code || item.itemCode || "",
  achievement: item.achievement || item.employeeAchievement || "",
  employeeRating: item.employeeRating ?? item.selfRating ?? "",
  managerRating: item.managerRating ?? "",
  secondLevelRating: item.secondLevelRating ?? "",
  calibratedRating: item.calibratedRating ?? "",
  employeeComment: item.employeeComment || item.comments || "",
  managerComment: item.managerComment || "",
  weightage: item.weightage ?? 0,
});

const normalizeReviewSections = (review = {}) => {
  const templateSections = review.templateSnapshot?.sections || [];
  const runtimeSections = review.sections || [];
  const sectionMap = new Map();

  templateSections.forEach((section, index) => {
    const key = normalizeSectionKey(section) || `TEMPLATE_${index}`;
    sectionMap.set(key, { ...section });
  });

  runtimeSections.forEach((section, index) => {
    const key = normalizeSectionKey(section) || `RUNTIME_${index}`;
    const existing = sectionMap.get(key) || {};
    const resolvedItems =
      Array.isArray(section.items) && section.items.length > 0
        ? section.items
        : existing.items || [];
    sectionMap.set(key, { ...existing, ...section, items: resolvedItems });
  });

  return Array.from(sectionMap.values())
    .map((section, index) => ({
      sectionId: section.sectionId || section.code || section._id || `SECTION_${index + 1}`,
      code: section.code,
      title: section.title || section.name || `Section ${index + 1}`,
      sectionType: section.sectionType || section.type || "CUSTOM",
      sequence: Number(section.sequence ?? index),
      weightage: Number(section.weightage ?? 0),
      calculationMethod: section.calculationMethod,
      itemSource: section.itemSource || section.sourceType || "MANUAL",
      mandatory: Boolean(section.mandatory),
      isRequired: Boolean(section.isRequired ?? section.mandatory),
      allowEmployeeRating: section.allowEmployeeRating !== false,
      allowManagerRating: section.allowManagerRating !== false,
      allowSecondLevelRating: Boolean(section.allowSecondLevelRating),
      allow360Rating: Boolean(section.allow360Rating),
      allowCalibration: Boolean(section.allowCalibration),
      allowComments: section.allowComments !== false,
      commentRequired: Boolean(section.commentRequired),
      allowEvidence: Boolean(section.allowEvidence),
      evidenceRequired: Boolean(section.evidenceRequired),
      visibleToEmployee: section.visibleToEmployee,
      visibleToManager: section.visibleToManager,
      visibleToSecondLevel: section.visibleToSecondLevel,
      visibleToHr: section.visibleToHr,
      isVisible: section.isVisible,
      enabled: section.enabled,
      items: (section.items || []).map(normalizeReviewItem),
      employeeSectionScore: section.employeeSectionScore,
      managerSectionScore: section.managerSectionScore,
      secondLevelSectionScore: section.secondLevelSectionScore,
      calibratedSectionScore: section.calibratedSectionScore,
      finalSectionScore: section.finalSectionScore,
    }))
    .sort((a, b) => a.sequence - b.sequence);
};

const getSectionTypeMeta = (section = {}) => {
  const sectionType = String(section.sectionType || section.type || "").toUpperCase();
  const isGoalsSection = ["GOAL", "GOALS", "KRA"].includes(sectionType);
  const isCompetencySection = ["COMPETENCY", "COMPETENCIES"].includes(sectionType);
  const isCustomSection = sectionType === "CUSTOM";
  return {
    sectionType,
    isGoalsSection,
    isCompetencySection,
    isCustomSection,
    itemLabel: isGoalsSection
      ? "Goal or objective"
      : isCompetencySection
        ? "Competency"
        : "Assessment item",
  };
};

const getEmptySectionMessage = (section = {}) => {
  const { isGoalsSection, isCompetencySection } = getSectionTypeMeta(section);
  if (isGoalsSection) {
    return "No employee goals are available for this review section. Goals must be mapped from the selected goal cycle before assessment.";
  }
  if (isCompetencySection) {
    return "No competencies are mapped to this review. Configure competencies in the performance template or competency master.";
  }
  return "No assessment items are configured for this section. Add manual items to the performance template.";
};

const resolveActorMode = ({ review, user, role }) => {
  const backendActor = review?.actorMode || review?.currentActorRole;
  if (backendActor) return String(backendActor).toUpperCase();

  const normalizedRole = String(role || user?.role || "").toUpperCase();
  if (["SUPERADMIN", "ADMIN"].includes(normalizedRole)) return ACTOR_MODES.ADMIN;
  if (["HRHEAD", "HREXECUTIVE", "HR"].includes(normalizedRole)) return ACTOR_MODES.HR;

  const currentUserId = String(user?._id || user?.id || "");
  const matches = [
    [ACTOR_MODES.EMPLOYEE, getEntityId(review?.employeeId)],
    [ACTOR_MODES.MANAGER, getEntityId(review?.managerId)],
    [ACTOR_MODES.SECOND_LEVEL_MANAGER, getEntityId(review?.secondLevelReviewerId)],
  ].filter(([, id]) => currentUserId && String(id || "") === currentUserId);

  return matches.length === 1 ? matches[0][0] : ACTOR_MODES.READ_ONLY;
};

const getWorkflowState = (review = {}) =>
  String(review.workflowState?.currentState || review.currentStage || review.finalStatus || "DRAFT").toUpperCase();

const requiresSelfAssessment = (review = {}) =>
  review.templateSnapshot?.workflowConfiguration?.selfAssessmentRequired !== false;

const getTrackerStage = (state) => {
  switch (String(state || "").toUpperCase()) {
    case "DRAFT":
    case "NOT_STARTED":
    case "SELF_ASSESSMENT":
    case "SELF_ASSESSMENT_IN_PROGRESS":
      return "SELF_ASSESSMENT";
    case "SELF_ASSESSMENT_SUBMITTED":
    case "MANAGER_REVIEW":
    case "MANAGER_ASSESSMENT":
    case "MANAGER_REVIEW_IN_PROGRESS":
      return "MANAGER_REVIEW";
    case "MANAGER_REVIEW_SUBMITTED":
    case "PENDING_CALIBRATION":
    case "CALIBRATION":
    case "CALIBRATED":
      return "CALIBRATION";
    case "PENDING_ACKNOWLEDGEMENT":
      return "ACKNOWLEDGEMENT";
    case "ACKNOWLEDGED":
    case "FINALIZED":
    case "COMPLETED":
    case "CLOSED":
      return "COMPLETED";
    default:
      return state;
  }
};

const isFinalWorkflow = (review = {}) =>
  FINAL_STATES.has(getWorkflowState(review)) || FINAL_STATES.has(String(review.finalStatus || "").toUpperCase());

const canViewSection = (section, actorMode) => {
  if (section.isVisible === false) return false;
  if (actorMode === ACTOR_MODES.EMPLOYEE && section.visibleToEmployee === false) return false;
  if (actorMode === ACTOR_MODES.MANAGER && section.visibleToManager === false) return false;
  if (actorMode === ACTOR_MODES.SECOND_LEVEL_MANAGER && section.visibleToSecondLevel === false) return false;
  if ([ACTOR_MODES.HR, ACTOR_MODES.ADMIN, ACTOR_MODES.CALIBRATOR].includes(actorMode) && section.visibleToHr === false) return false;
  return true;
};

const canEditSecondLevelRating = (section, actorMode, workflowState, finalWorkflow) =>
  !finalWorkflow &&
  section.enabled !== false &&
  actorMode === ACTOR_MODES.SECOND_LEVEL_MANAGER &&
  SECOND_LEVEL_EDIT_STATES.has(workflowState) &&
  section.allowSecondLevelRating === true;

const canEditCalibratedRating = (section, actorMode, workflowState, finalWorkflow) =>
  !finalWorkflow &&
  section.enabled !== false &&
  [ACTOR_MODES.HR, ACTOR_MODES.ADMIN, ACTOR_MODES.CALIBRATOR].includes(actorMode) &&
  CALIBRATION_STATES.has(workflowState) &&
  section.allowCalibration === true;

const canViewSelfAssessment = (actorMode) =>
  [
    ACTOR_MODES.EMPLOYEE,
    ACTOR_MODES.MANAGER,
    ACTOR_MODES.SECOND_LEVEL_MANAGER,
    ACTOR_MODES.HR,
    ACTOR_MODES.ADMIN,
    ACTOR_MODES.CALIBRATOR,
  ].includes(actorMode);

const canViewManagerReview = (actorMode) =>
  [
    ACTOR_MODES.MANAGER,
    ACTOR_MODES.SECOND_LEVEL_MANAGER,
    ACTOR_MODES.HR,
    ACTOR_MODES.ADMIN,
    ACTOR_MODES.CALIBRATOR,
  ].includes(actorMode);

const getDisplayedSectionScore = ({ section, actorMode, assessmentMode }) => {
  if (actorMode === ACTOR_MODES.EMPLOYEE) return section.employeeSectionScore ?? null;
  if (assessmentMode === "SELF") return section.employeeSectionScore ?? null;
  if (assessmentMode === "MANAGER" && canViewManagerReview(actorMode)) return section.managerSectionScore ?? null;
  if (actorMode === ACTOR_MODES.SECOND_LEVEL_MANAGER) return section.secondLevelSectionScore;
  if ([ACTOR_MODES.HR, ACTOR_MODES.ADMIN, ACTOR_MODES.CALIBRATOR].includes(actorMode)) return section.finalSectionScore ?? section.calibratedSectionScore ?? null;
  return null;
};

const resolveRatingValues = (review = {}) => {
  const scaleCandidates = [
    review?.templateSnapshot?.ratingScaleSnapshot,
    review?.ratingScaleSnapshot,
    review?.ratingScale,
    review?.templateSnapshot?.ratingScale,
  ];

  const values =
    scaleCandidates
      .map((scale) => scale?.values || scale?.levels || scale?.ratings || [])
      .find((scaleValues) => Array.isArray(scaleValues) && scaleValues.length > 0) || [];

  return values.sort(
    (a, b) => Number(a.sequence ?? a.value ?? 0) - Number(b.sequence ?? b.value ?? 0),
  );
};

const getQuickFillRatingValue = (ratingValues = []) => {
  const rating = [...ratingValues].sort(
    (a, b) => Number(b.value ?? 0) - Number(a.value ?? 0),
  )[0];
  return rating?.value ?? "";
};

const buildQuickFillDrafts = ({
  sections = [],
  ratingValues = [],
  assessmentMode,
  selfAssessmentEditable,
  managerReviewEditable,
}) => {
  const ratingValue = getQuickFillRatingValue(ratingValues);
  const drafts = {};
  let updatedItemCount = 0;

  sections.forEach((section) => {
    if (section.enabled === false || !(section.items || []).length) return;

    const sectionDraft = { items: {} };
    section.items.forEach((item, index) => {
      const itemDraft = {};

      if (assessmentMode === "SELF" && selfAssessmentEditable) {
        itemDraft.achievement = `Quick fill achievement ${index + 1}`;
        if (section.allowEmployeeRating && ratingValue !== "") {
          itemDraft.employeeRating = ratingValue;
        }
        if (section.allowComments) {
          itemDraft.employeeComment = `Quick fill self comment ${index + 1}`;
        }
      }

      if (assessmentMode === "MANAGER" && managerReviewEditable) {
        if (section.allowManagerRating && ratingValue !== "") {
          itemDraft.managerRating = ratingValue;
        }
        if (section.allowComments) {
          itemDraft.managerComment = `Quick fill manager comment ${index + 1}`;
        }
      }

      if (Object.keys(itemDraft).length > 0) {
        sectionDraft.items[item.itemId] = itemDraft;
        updatedItemCount += 1;
      }
    });

    if (Object.keys(sectionDraft.items).length > 0) {
      drafts[section.sectionId] = sectionDraft;
    }
  });

  return { drafts, updatedItemCount };
};

const ReviewDetailPage = () => {
  const { reviewId } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { role, can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const [showGuide, setShowGuide] = React.useState(false);
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subtleBg = useColorModeValue("gray.50", "gray.900");
  const mutedText = useColorModeValue("gray.600", "gray.400");
  const [activeSectionId, setActiveSectionId] = React.useState(null);
  const [assessmentMode, setAssessmentMode] = React.useState("SELF");
  const [sectionDrafts, setSectionDrafts] = React.useState({});
  const [acknowledged, setAcknowledged] = React.useState(false);

  const canReadReviews = can(PERFORMANCE_PERMISSIONS.REVIEWS_READ);
  const canApproveRecommendations = can(PERFORMANCE_PERMISSIONS.REVIEW_APPROVE);

  if (!canReadReviews) {
    return (
      <ModulePageShell
        title="Review Detail"
        description="Process submit, acknowledge, finalize, increment, and promotion workflows."
      >
        <EmptyState message="You do not have permission to view review details." />
      </ModulePageShell>
    );
  }

  const reviewQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.reviewDetail, reviewId, user?._id || user?.id],
    queryFn: () => reviewApi.getReviewById(reviewId),
    enabled: Boolean(reviewId),
  });

  const refreshReview = () =>
    queryClient.invalidateQueries({
      queryKey: [PERFORMANCE_QUERY_KEYS.reviewDetail, reviewId],
    });

  const submitMutation = useMutation({
    mutationFn: (payload) =>
      assessmentMode === "MANAGER"
        ? reviewApi.submitManagerAssessment(reviewId, payload)
        : reviewApi.submitSelfAssessment(reviewId, payload),
    onSuccess: refreshReview,
  });
  const saveDraftMutation = useMutation({
    mutationFn: (payload) =>
      assessmentMode === "MANAGER"
        ? reviewApi.saveManagerAssessment(reviewId, payload)
        : reviewApi.saveSelfAssessment(reviewId, payload),
    onSuccess: () => {
      setSectionDrafts({});
      refreshReview();
    },
  });
  const acknowledgeMutation = useMutation({
    mutationFn: (payload) => reviewApi.acknowledgeReview(reviewId, payload),
    onSuccess: refreshReview,
  });
  const finalizeMutation = useMutation({
    mutationFn: () => reviewApi.finalizeReview(reviewId),
    onSuccess: refreshReview,
  });
  const incrementMutation = useMutation({
    mutationFn: (payload) => reviewApi.approveIncrement(reviewId, payload),
    onSuccess: refreshReview,
  });
  const promotionMutation = useMutation({
    mutationFn: (payload) => reviewApi.approvePromotion(reviewId, payload),
    onSuccess: refreshReview,
  });

  const { register, handleSubmit } = useForm({
    defaultValues: { remarks: "" },
  });
  const {
    register: registerIncrement,
    handleSubmit: handleIncrementSubmit,
    reset: resetIncrement,
  } =
    useForm({
      defaultValues: {
        recommended: true,
        percentage: 0,
        amount: 0,
        currency: "INR",
        remarks: "",
      },
    });
  const {
    register: registerPromotion,
    handleSubmit: handlePromotionSubmit,
    reset: resetPromotion,
  } =
    useForm({
      defaultValues: { recommended: true, designation: "", remarks: "" },
    });

  const showActionToast = (title) =>
    toast({ title, status: "success", duration: 2000 });

  const review = reviewQuery.data?.data;
  const actorMode = resolveActorMode({ review, user, role });
  const workflowState = getWorkflowState(review);
  const selfAssessmentRequired = requiresSelfAssessment(review);
  const trackerStage = getTrackerStage(workflowState);
  const finalWorkflow = isFinalWorkflow(review);
  const isHrActor = actorMode === ACTOR_MODES.HR;
  const isAdminActor = actorMode === ACTOR_MODES.ADMIN;
  const currentUserId = user?._id || user?.id;
  const isReviewEmployee = idsMatch(currentUserId, getEntityId(review?.employeeId || review?.employee));
  const isReviewManager = idsMatch(currentUserId, getEntityId(review?.managerId || review?.manager));
  const isElevatedActor = isHrActor || isAdminActor;
  const sections = React.useMemo(() => normalizeReviewSections(review), [review]);
  const visibleSections = React.useMemo(
    () => sections.filter((section) => canViewSection(section, actorMode)),
    [sections, actorMode],
  );
  const activeSection =
    visibleSections.find((section) => String(section.sectionId) === String(activeSectionId)) ||
    visibleSections[0];
  const ratingValues = React.useMemo(
    () => resolveRatingValues(review),
    [review],
  );
  const finalScore = review?.finalTotalScore ?? review?.finalScore ?? review?.overallRating ?? 0;
  const finalRatingValue = review?.finalRating ?? review?.overallRating;
  const finalRating =
    review?.finalRatingLabel ||
    ratingValues.find((rating) => String(rating.value) === String(finalRatingValue))?.label ||
    finalRatingValue ||
    "-";
  const performanceCategory =
    review?.finalCategory?.label ||
    review?.finalCategory?.name ||
    review?.performanceCategory ||
    "-";
  const allowedActions = review?.allowedActions || [];

  React.useEffect(() => {
    if (!review) return;
    resetIncrement({
      recommended: review.incrementRecommendation?.recommended ?? true,
      percentage: review.incrementRecommendation?.percentage ?? 0,
      amount: review.incrementRecommendation?.amount ?? 0,
      currency: review.incrementRecommendation?.currency || "INR",
      remarks: review.incrementRecommendation?.remarks || "",
    });
    resetPromotion({
      recommended: review.promotionRecommendation?.recommended ?? true,
      designation: review.promotionRecommendation?.designation || "",
      remarks: review.promotionRecommendation?.remarks || "",
    });
  }, [review, resetIncrement, resetPromotion]);
  const reviewAccess = React.useMemo(
    () => ({
      actorMode,
      selfAssessment: {
        view: selfAssessmentRequired && (isReviewEmployee || isReviewManager || isElevatedActor),
        edit:
          selfAssessmentRequired &&
          isReviewEmployee &&
          EMPLOYEE_EDIT_STATES.has(workflowState) &&
          !finalWorkflow,
      },
      managerReview: {
        view: isReviewManager || isElevatedActor,
        edit:
          isReviewManager &&
          MANAGER_EDIT_STATES.has(workflowState) &&
          !finalWorkflow,
      },
    }),
    [
      actorMode,
      selfAssessmentRequired,
      isReviewEmployee,
      isReviewManager,
      isElevatedActor,
      workflowState,
      finalWorkflow,
    ],
  );
  const canSaveDraft =
    assessmentMode === "MANAGER"
      ? reviewAccess.managerReview.edit && allowedActions.includes("SAVE_MANAGER_REVIEW")
      : reviewAccess.selfAssessment.edit && allowedActions.includes("SAVE_SELF_ASSESSMENT");
  const canSubmit =
    assessmentMode === "MANAGER"
      ? reviewAccess.managerReview.edit && allowedActions.includes("SUBMIT_MANAGER_REVIEW")
      : reviewAccess.selfAssessment.edit && allowedActions.includes("SUBMIT_SELF_ASSESSMENT");
  const canQuickFillAssessment =
    visibleSections.some(
      (section) => section.enabled !== false && (section.items || []).length > 0,
    ) &&
    ((assessmentMode === "SELF" && reviewAccess.selfAssessment.edit) ||
      (assessmentMode === "MANAGER" && reviewAccess.managerReview.edit));
  const canAcknowledge = allowedActions.includes("ACKNOWLEDGE_REVIEW");
  const canViewScoresAndResults = isReviewManager || isElevatedActor;
  const canFinalize =
    allowedActions.includes("FINALIZE_REVIEW") &&
    (isReviewManager || isElevatedActor) &&
    canViewScoresAndResults;
  const canApproveIncrement = allowedActions.includes("APPROVE_INCREMENT") && canApproveRecommendations;
  const canApprovePromotion = allowedActions.includes("APPROVE_PROMOTION") && canApproveRecommendations;
  const canViewApprovalPanels =
    canApproveRecommendations &&
    ["SUPERADMIN", "ADMIN", "HRHEAD"].includes(String(role || "").toUpperCase());
  const showApprovals =
    canViewApprovalPanels;

  React.useEffect(() => {
    if (!visibleSections.length) {
      setActiveSectionId(null);
      return;
    }
    if (!activeSection || !activeSectionId) {
      setActiveSectionId(visibleSections[0].sectionId);
    }
  }, [activeSection, activeSectionId, visibleSections]);

  React.useEffect(() => {
    setAssessmentMode(selfAssessmentRequired ? "SELF" : "MANAGER");
    setSectionDrafts({});
    setActiveSectionId(null);
  }, [reviewId, user?._id, user?.id, selfAssessmentRequired]);

  React.useEffect(() => {
    if (assessmentMode === "MANAGER" && !reviewAccess.managerReview.view) {
      setAssessmentMode("SELF");
    }
  }, [assessmentMode, reviewAccess.managerReview.view]);

  const updateDraftItem = (sectionId, itemId, field, value) => {
    setSectionDrafts((current) => ({
      ...current,
      [sectionId]: {
        ...(current[sectionId] || {}),
        items: {
          ...(current[sectionId]?.items || {}),
          [itemId]: {
            ...(current[sectionId]?.items?.[itemId] || {}),
            [field]: value,
          },
        },
      },
    }));
  };

  const getDraftValue = (section, item, field) =>
    sectionDrafts[section.sectionId]?.items?.[item.itemId]?.[field] ?? item[field] ?? "";

  const buildEmployeeAssessmentPayload = () => ({
    sections: sections.map((section) => ({
      sectionId: section.sectionId,
      items: section.items.map((item) => ({
        itemId: item.itemId,
        achievement: getDraftValue(section, item, "achievement"),
        employeeRating: getDraftValue(section, item, "employeeRating"),
        employeeComment: getDraftValue(section, item, "employeeComment"),
        employeeEvidence: getDraftValue(section, item, "employeeEvidence") || [],
      })),
    })),
  });

  const buildManagerReviewPayload = () => ({
    sections: sections.map((section) => ({
      sectionId: section.sectionId,
      items: section.items.map((item) => ({
        itemId: item.itemId,
        managerRating: getDraftValue(section, item, "managerRating"),
        managerComment: getDraftValue(section, item, "managerComment"),
      })),
    })),
  });

  const buildAssessmentPayload = () =>
    assessmentMode === "MANAGER"
      ? buildManagerReviewPayload()
      : buildEmployeeAssessmentPayload();

  const quickFillTestCase = () => {
    const { drafts, updatedItemCount } = buildQuickFillDrafts({
      sections: visibleSections,
      ratingValues,
      assessmentMode,
      selfAssessmentEditable: reviewAccess.selfAssessment.edit,
      managerReviewEditable: reviewAccess.managerReview.edit,
    });

    setSectionDrafts((current) => {
      const nextDrafts = { ...current };
      Object.entries(drafts).forEach(([sectionId, sectionDraft]) => {
        nextDrafts[sectionId] = {
          ...(nextDrafts[sectionId] || {}),
          items: {
            ...(nextDrafts[sectionId]?.items || {}),
            ...sectionDraft.items,
          },
        };
      });
      return nextDrafts;
    });

    toast({
      title: "Test data filled",
      description: `${updatedItemCount} review item${updatedItemCount === 1 ? "" : "s"} updated.`,
      status: "success",
      duration: 2000,
    });
  };

  return (
    <ModulePageShell
      title="Review Detail"
      description="Process submit, acknowledge, finalize, increment, and promotion workflows."
      onRefresh={() => reviewQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {reviewQuery.isLoading ? (
        <LoadingState />
      ) : !review ? (
        <EmptyState message="Review not found." />
      ) : (
        <VStack align="stretch" spacing={4}>
          <OperationalPanel>
            <Flex justify="space-between" align={{ base: "stretch", md: "flex-start" }} direction={{ base: "column", md: "row" }} gap={3}>
              <Box minW={0}>
                <HStack wrap="wrap" spacing={2}>
                  <Text fontSize="lg" fontWeight="semibold">
                    {review.name || review.title || "Annual appraisal"}
                  </Text>
                  <StatusBadge value={review.status || review.finalStatus || "IN_PROGRESS"} />
                </HStack>
                <Text mt={1} fontSize="sm" color={mutedText}>
                  {actorMode.replaceAll("_", " ")} · {workflowState.replaceAll("_", " ")} · Due date: {formatDate(review.dueDate)}
                </Text>
              </Box>
              <HStack wrap="wrap" justify={{ base: "flex-start", md: "flex-end" }}>
                <Text fontSize="xs" color={mutedText}>
                  {review.lastSavedAt ? `Saved at ${formatDate(review.lastSavedAt)}` : "Explicit save required"}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={!canSaveDraft}
                  isLoading={saveDraftMutation.isPending}
                  onClick={async () => {
                    await saveDraftMutation.mutateAsync(buildAssessmentPayload());
                    showActionToast(
                      assessmentMode === "MANAGER"
                        ? "Manager review saved"
                        : "Self-assessment saved",
                    );
                  }}
                >
                  {assessmentMode === "MANAGER"
                    ? "Save manager review"
                    : "Save self-assessment"}
                </Button>
                {canSubmit ? (
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={async () => {
                      await submitMutation.mutateAsync(buildAssessmentPayload());
                      showActionToast(
                        assessmentMode === "MANAGER"
                          ? "Manager review submitted"
                          : "Self-assessment submitted",
                      );
                    }}
                    isLoading={submitMutation.isPending}
                  >
                    {assessmentMode === "MANAGER"
                      ? "Submit manager review"
                      : "Submit self-assessment"}
                  </Button>
                ) : null}
              </HStack>
            </Flex>
            <Divider my={4} />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <Box>
                <Text fontSize="sm" color={mutedText}>Employee</Text>
                <Text fontWeight="semibold">{personName(review.employeeId || review.employee)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color={mutedText}>Manager</Text>
                <Text fontWeight="semibold">{personName(review.managerId || review.manager)}</Text>
              </Box>
              {canViewScoresAndResults ? (
                <Box>
                  <Text fontSize="sm" color={mutedText}>
                    {assessmentMode === "MANAGER"
                      ? "Manager review score"
                      : "Self-assessment score"}
                  </Text>
                  <Badge colorScheme="green">
                    {assessmentMode === "MANAGER"
                      ? review.managerTotalScore ?? 0
                      : review.employeeTotalScore ?? 0}
                  </Badge>
                </Box>
              ) : null}
            </SimpleGrid>
          </OperationalPanel>

          <OperationalPanel>
            <Text fontWeight="semibold" mb={3}>
              Review workflow
            </Text>
            <ReviewProgressTracker stages={review.workflowStages || review.workflow?.stages} currentStage={trackerStage} />
          </OperationalPanel>

          <VStack align="stretch" spacing={4}>
            <OperationalPanel>
              <Text fontWeight="semibold" mb={3}>
                Sections
              </Text>
              <VStack align="stretch" spacing={2}>
                {visibleSections.length === 0 ? (
                  <Text fontSize="sm" color={mutedText}>No template sections available.</Text>
                ) : (
                  visibleSections.map((section) => {
                    const sectionScore = getDisplayedSectionScore({
                      section,
                      actorMode,
                      assessmentMode,
                    });
                    const editable =
                      (assessmentMode === "SELF" && reviewAccess.selfAssessment.edit) ||
                      (assessmentMode === "MANAGER" && reviewAccess.managerReview.edit) ||
                      canEditSecondLevelRating(section, actorMode, workflowState, finalWorkflow) ||
                      canEditCalibratedRating(section, actorMode, workflowState, finalWorkflow);
                    const status = section.enabled === false
                      ? "Disabled"
                      : section.items.length === 0
                        ? "Not configured"
                        : editable
                          ? "Editable"
                          : "Read only";
                    return (
                    <Button
                      key={section.sectionId}
                      size="sm"
                      h="auto"
                      minH="54px"
                      py={2}
                      variant={section.sectionId === activeSection?.sectionId ? "solid" : "ghost"}
                      colorScheme={section.sectionId === activeSection?.sectionId ? "blue" : "gray"}
                      justifyContent="space-between"
                      onClick={() => setActiveSectionId(section.sectionId)}
                    >
                      <Box textAlign="left" minW={0} flex="1">
                        <Text as="span" display="block" whiteSpace="normal" noOfLines={2}>
                          {section.title}
                        </Text>
                        <HStack mt={1} spacing={1} wrap="wrap">
                          <Badge>{section.weightage || 0}%</Badge>
                          <Badge colorScheme={editable ? "green" : section.items.length === 0 ? "orange" : "gray"}>
                            {status}
                          </Badge>
                        </HStack>
                      </Box>
                      {canViewScoresAndResults ? (
                        <Text as="span" fontSize="xs" ml={2}>
                          {sectionScore ?? "-"}
                        </Text>
                      ) : null}
                    </Button>
                    );
                  })
                )}
              </VStack>
            </OperationalPanel>

            <OperationalPanel>
              <Flex justify="space-between" align={{ base: "stretch", sm: "center" }} mb={3} wrap="wrap" gap={3}>
                <Box minW={0} flex="1">
                  <Text fontWeight="semibold">{activeSection?.title || activeSection?.name || "Review section"}</Text>
                  {canViewScoresAndResults ? (
                    <Text fontSize="sm" color={mutedText}>
                      Estimated section score until backend calculation confirms it.
                    </Text>
                  ) : null}
                </Box>
                <Badge colorScheme="blue">
                  {activeSection?.weightage || 0}% weight
                </Badge>
              </Flex>
              <HStack mb={4} spacing={2} wrap="wrap" align="stretch">
                {selfAssessmentRequired && reviewAccess.selfAssessment.view ? (
                  <Button
                    size="sm"
                    variant={assessmentMode === "SELF" ? "solid" : "ghost"}
                    colorScheme={assessmentMode === "SELF" ? "blue" : "gray"}
                    onClick={() => setAssessmentMode("SELF")}
                  >
                    Self-assessment
                  </Button>
                ) : null}
                {reviewAccess.managerReview.view ? (
                  <Button
                    size="sm"
                    variant={assessmentMode === "MANAGER" ? "solid" : "ghost"}
                    colorScheme={assessmentMode === "MANAGER" ? "blue" : "gray"}
                    onClick={() => setAssessmentMode("MANAGER")}
                  >
                    Manager review
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={!canQuickFillAssessment}
                  onClick={quickFillTestCase}
                >
                  Quick fill test case
                </Button>
              </HStack>
              {activeSection?.enabled === false ? (
                <Box bg={subtleBg} borderRadius="md" p={4}>
                  <Badge mb={2}>Disabled</Badge>
                  <Text fontSize="sm" color={mutedText}>
                    This section is unavailable for the current workflow stage.
                  </Text>
                </Box>
              ) : activeSection?.items?.length === 0 ? (
                <Box bg={subtleBg} borderRadius="md" p={4}>
                  <Badge colorScheme="orange" mb={2}>Not configured</Badge>
                  <Text fontSize="sm" fontWeight="semibold">{getEmptySectionMessage(activeSection)}</Text>
                  <Text mt={2} fontSize="sm" color={mutedText}>
                    Source: {activeSection.itemSource || "-"}
                  </Text>
                </Box>
              ) : (
                <TableContainer overflowX="auto">
                  <Table
                    size="sm"
                    tableLayout="fixed"
                    minW={{
                      base: assessmentMode === "MANAGER" ? "1120px" : "760px",
                      md: assessmentMode === "MANAGER" ? "1320px" : "960px",
                    }}
                  >
                    <Thead>
                      <Tr>
                        <Th
                          position="sticky"
                          left={0}
                          zIndex={1}
                          bg={bg}
                          w={{ base: "240px", sm: "300px", lg: "44%" }}
                          maxW={{ base: "240px", sm: "300px", lg: "44%" }}
                          boxShadow="1px 0 0 var(--chakra-colors-gray-200)"
                        >
                          {getSectionTypeMeta(activeSection).itemLabel}
                        </Th>
                        <Th isNumeric w="110px">Weightage</Th>
                        {assessmentMode === "SELF" ? (
                          <>
                            <Th w="180px">Achievement</Th>
                            <Th w="170px">Self-rating</Th>
                            <Th w="230px">Your comment</Th>
                          </>
                        ) : null}
                        {assessmentMode === "MANAGER" && reviewAccess.managerReview.view ? (
                          <>
                            <Th w="180px">Employee achievement</Th>
                            <Th w="150px">Employee rating</Th>
                            <Th w="220px">Employee comment</Th>
                            <Th w="170px">Manager rating</Th>
                            <Th w="230px">Manager comment</Th>
                          </>
                        ) : null}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {activeSection.items.map((item) => {
                        const employeeRatingEditable =
                          reviewAccess.selfAssessment.edit &&
                          activeSection.allowEmployeeRating &&
                          ratingValues.length > 0;
                        const managerRatingEditable =
                          reviewAccess.managerReview.edit &&
                          activeSection.allowManagerRating &&
                          ratingValues.length > 0;
                        return (
                          <Tr key={item.itemId}>
                            <Td
                              position="sticky"
                              left={0}
                              zIndex={1}
                              bg={bg}
                              maxW={{ base: "240px", sm: "300px", lg: "44%" }}
                              boxShadow="1px 0 0 var(--chakra-colors-gray-200)"
                            >
                              <Text fontSize="sm" fontWeight="semibold" whiteSpace="normal">
                                {item.title}
                              </Text>
                              <Text
                                fontSize="xs"
                                color={mutedText}
                                whiteSpace="normal"
                                overflowWrap="anywhere"
                                noOfLines={2}
                              >
                                {item.description || "-"}
                              </Text>
                            </Td>
                            <Td isNumeric w="110px">{item.weightage || 0}%</Td>
                            {assessmentMode === "SELF" ? (
                              <>
                                <Td w="180px">
                                  <Input
                                    size="sm"
                                    w="100%"
                                    value={getDraftValue(activeSection, item, "achievement")}
                                    onChange={(event) => updateDraftItem(activeSection.sectionId, item.itemId, "achievement", event.target.value)}
                                    isDisabled={!reviewAccess.selfAssessment.edit}
                                  />
                                </Td>
                                <Td w="170px">
                                  <Select
                                    size="sm"
                                    w="100%"
                                    placeholder={ratingValues.length ? "Select" : "Rating scale is not configured."}
                                    value={getDraftValue(activeSection, item, "employeeRating")}
                                    onChange={(event) => updateDraftItem(activeSection.sectionId, item.itemId, "employeeRating", event.target.value)}
                                    isDisabled={!employeeRatingEditable}
                                  >
                                    {ratingValues.map((rating) => (
                                      <option key={rating.value} value={rating.value}>
                                        {rating.value} - {rating.label}
                                      </option>
                                    ))}
                                  </Select>
                                </Td>
                                <Td w="230px">
                                  <Textarea
                                    size="sm"
                                    rows={1}
                                    w="100%"
                                    value={getDraftValue(activeSection, item, "employeeComment")}
                                    onChange={(event) => updateDraftItem(activeSection.sectionId, item.itemId, "employeeComment", event.target.value)}
                                    isDisabled={!reviewAccess.selfAssessment.edit || !activeSection.allowComments}
                                  />
                                </Td>
                              </>
                            ) : null}
                            {assessmentMode === "MANAGER" && reviewAccess.managerReview.view ? (
                              <>
                                <Td w="180px">
                                  <Input
                                    size="sm"
                                    w="100%"
                                    value={getDraftValue(activeSection, item, "achievement")}
                                    isDisabled
                                  />
                                </Td>
                                <Td w="150px">
                                  <Text fontSize="sm">
                                    {item.employeeRating ? String(item.employeeRating) : "-"}
                                  </Text>
                                </Td>
                                <Td w="220px">
                                  <Text fontSize="sm" whiteSpace="pre-wrap">
                                    {item.employeeComment || "-"}
                                  </Text>
                                </Td>
                              <Td w="170px">
                                <Select
                                  size="sm"
                                  w="100%"
                                  placeholder={ratingValues.length ? "Select" : "Rating scale is not configured."}
                                  value={getDraftValue(activeSection, item, "managerRating")}
                                  onChange={(event) => updateDraftItem(activeSection.sectionId, item.itemId, "managerRating", event.target.value)}
                                  isDisabled={!managerRatingEditable}
                                >
                                  {ratingValues.map((rating) => (
                                    <option key={rating.value} value={rating.value}>
                                      {rating.value} - {rating.label}
                                    </option>
                                  ))}
                                </Select>
                              </Td>
                              <Td w="230px">
                                <Textarea
                                  size="sm"
                                  rows={1}
                                  w="100%"
                                  value={getDraftValue(activeSection, item, "managerComment")}
                                  onChange={(event) => updateDraftItem(activeSection.sectionId, item.itemId, "managerComment", event.target.value)}
                                  isDisabled={!reviewAccess.managerReview.edit || !activeSection.allowComments}
                                />
                              </Td>
                              </>
                            ) : null}
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
              {canViewScoresAndResults ? (
                <Flex justify={{ base: "space-between", sm: "flex-end" }} align="center" gap={3} pt={3} mt={3} borderTopWidth="1px" borderColor={borderColor} wrap="wrap">
                  <Text fontSize="sm">Section score</Text>
                  <Badge colorScheme="green" fontSize="sm">
                    {(() => {
                      const score = activeSection
                        ? getDisplayedSectionScore({ section: activeSection, actorMode, assessmentMode })
                        : null;
                      return score === null || score === undefined ? "Not calculated" : score;
                    })()}
                  </Badge>
                </Flex>
              ) : null}
            </OperationalPanel>
          </VStack>

          {canViewScoresAndResults ? (
          <OperationalPanel>
            <SimpleGrid columns={{ base: 1, lg: canAcknowledge ? 2 : 1 }} spacing={4}>
              <Box bg={subtleBg} borderRadius="md" p={4}>
                <Text fontWeight="semibold">Overall result</Text>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={3}>
                  <Text fontSize="sm" color={mutedText}>Final score</Text>
                  <Text fontSize="sm" fontWeight="semibold">{finalScore}</Text>
                  <Text fontSize="sm" color={mutedText}>Final rating</Text>
                  <Text fontSize="sm" fontWeight="semibold">{finalRating}</Text>
                  <Text fontSize="sm" color={mutedText}>Performance category</Text>
                  <Text fontSize="sm" fontWeight="semibold">{performanceCategory}</Text>
                </SimpleGrid>
              </Box>
              {canAcknowledge ? (
                <Box>
                  <Text fontWeight="semibold" mb={3}>Declaration</Text>
                  <Checkbox isChecked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)}>
                    I acknowledge that I have received and reviewed this performance assessment.
                  </Checkbox>
                  <HStack mt={3} justify="flex-end">
                    <Button
                      size="sm"
                      colorScheme="blue"
                      isDisabled={!acknowledged}
                      onClick={handleSubmit(async (values) => {
                        await acknowledgeMutation.mutateAsync(values);
                        showActionToast("Review acknowledged");
                      })}
                      isLoading={acknowledgeMutation.isPending}
                    >
                      Acknowledge
                    </Button>
                  </HStack>
                </Box>
              ) : null}
            </SimpleGrid>
          </OperationalPanel>
          ) : null}

          {canFinalize ? (
            <OperationalPanel>
              <HStack justify={{ base: "stretch", md: "flex-end" }}>
                <Button
                  width={{ base: "100%", md: "auto" }}
                  onClick={async () => {
                    await finalizeMutation.mutateAsync();
                    showActionToast("Review finalized");
                  }}
                  isLoading={finalizeMutation.isPending}
                >
                  Finalize
                </Button>
              </HStack>
            </OperationalPanel>
          ) : null}

          {showApprovals ? (
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="bold" mb={3}>
                Increment Approval
              </Text>
              <form
                onSubmit={handleIncrementSubmit(async (values) => {
                  await incrementMutation.mutateAsync({
                    ...values,
                    recommended: true,
                    percentage: Number(values.percentage) || 0,
                    amount: Number(values.amount) || 0,
                  });
                  showActionToast("Increment approved");
                })}
              >
                <VStack align="stretch" spacing={2}>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Percentage"
                    {...registerIncrement("percentage")}
                  />
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Amount"
                    {...registerIncrement("amount")}
                  />
                  <Input
                    placeholder="Currency"
                    {...registerIncrement("currency")}
                  />
                  <Input
                    placeholder="Remarks"
                    {...registerIncrement("remarks")}
                  />
                  <Button
                    type="submit"
                    isLoading={incrementMutation.isPending}
                    isDisabled={!canApproveIncrement}
                  >
                    Approve Increment
                  </Button>
                </VStack>
              </form>
            </Box>

            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="bold" mb={3}>
                Promotion Approval
              </Text>
              <form
                onSubmit={handlePromotionSubmit(async (values) => {
                  await promotionMutation.mutateAsync({
                    ...values,
                    recommended: true,
                  });
                  showActionToast("Promotion approved");
                })}
              >
                <VStack align="stretch" spacing={2}>
                  <Input
                    placeholder="Designation"
                    {...registerPromotion("designation")}
                  />
                  <Input
                    placeholder="Remarks"
                    {...registerPromotion("remarks")}
                  />
                  <Button
                    type="submit"
                    isLoading={promotionMutation.isPending}
                    isDisabled={!canApprovePromotion}
                  >
                    Approve Promotion
                  </Button>
                </VStack>
              </form>
            </Box>
          </SimpleGrid>
          ) : null}
        </VStack>
      )}
    </ModulePageShell>
  );
};

export default ReviewDetailPage;
